import * as React from "react";
import { Button, useBrandColors } from "../../ui";
import { usePrefersReducedMotion } from "../../../hooks/usePrefersReducedMotion";

const FLOAT_GAP = 6;

export type SlidingToggleOption<T extends string> = {
  value: T;
  label: string;
  /** Shown after the label, smaller: a count or a small icon. */
  adornment?: React.ReactNode;
};

/**
 * A two-way toggle in the section tabs' colours: a raised thumb slides under the selected option (orange), over a
 * pressed-in track (the other, in the tabs' grey).
 */
export const SlidingToggle = <T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  floating = false,
  attached = false,
}: {
  options: [SlidingToggleOption<T>, SlidingToggleOption<T>];
  value: T;
  onChange: (next: T) => void;
  ariaLabel: string;
  /** The thumb sits inset from the track's edges, so it reads as a button floating over a recessed track. */
  floating?: boolean;
  /** Full width under a card header: square outer corners and only a bottom border, the header drawing the top. */
  attached?: boolean;
}) => {
  const { primary } = useBrandColors();
  const still = usePrefersReducedMotion();
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value)
  );
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="relative flex rounded border border-[#44576e] bg-[#16202e] overflow-hidden"
      style={{
        boxShadow: "inset 0 2px 4px rgba(0,0,0,0.5)",
        ...(floating ? { padding: FLOAT_GAP, borderRadius: 8 } : {}),
        ...(attached ? { borderRadius: 0, borderWidth: "0 0 1px 0" } : {}),
      }}
    >
      <span
        aria-hidden
        className="absolute rounded bg-[#2b3a4f]"
        style={{
          // Floating: inset by the gap on every side; each half then ends the same gap from its edge.
          top: floating ? FLOAT_GAP : 0,
          bottom: floating ? FLOAT_GAP : 0,
          left: floating ? FLOAT_GAP : 0,
          width: floating ? `calc(50% - ${FLOAT_GAP}px)` : "50%",
          borderRadius: floating ? 6 : undefined,
          transform: `translateX(${selectedIndex * 100}%)`,
          transition: still ? "none" : "transform 220ms cubic-bezier(0.4, 0, 0.2, 1)",
          backgroundImage: "linear-gradient(rgba(255,255,255,0.06), rgba(0,0,0,0.06))",
          boxShadow: "0 2px 5px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.12)",
        }}
      />
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <Button
            key={option.value}
            aria-pressed={selected}
            onClick={() => onChange(option.value)}
            className="flex-1"
            style={{
              textTransform: "none",
              fontSize: "0.8125rem",
              fontWeight: 600,
              // 44px tap target in both modes.
              minHeight: 44,
              borderRadius: floating ? 6 : 4,
              color: selected ? primary : "rgba(255, 255, 255, 0.7)",
              transition: still ? "none" : "color 220ms",
              backgroundColor: "transparent",
            }}
          >
            <span className="flex items-center justify-center gap-1 whitespace-nowrap">
              {option.label}
              {option.adornment}
            </span>
          </Button>
        );
      })}
    </div>
  );
};
